<template>
  <div class="space-y-6">
    <!-- Header with Actions -->
    <div class="flex justify-between items-center">
      <div class="flex items-center space-x-4">
        <h2 class="text-2xl font-bold text-gray-900">Email Campaigns</h2>
        <span v-if="currentDraftId" class="text-sm text-gray-500">
          Editing: {{ currentDraftName }}
        </span>
        <span v-if="hasUnsavedChanges" class="text-xs text-orange-500 font-medium">
          (unsaved changes)
        </span>
      </div>
      <div class="flex space-x-3">
        <button
          @click="showDraftsPanel = !showDraftsPanel"
          class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
        >
          {{ showDraftsPanel ? 'Hide Drafts' : 'Drafts' }} ({{ drafts.length }})
        </button>
        <button
          @click="saveDraft"
          :disabled="saving || !campaignData.subject"
          class="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {{ saving ? 'Saving...' : 'Save Draft' }}
        </button>
        <button
          @click="showPreview = true"
          :disabled="!campaignData.subject"
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Preview
        </button>
        <button
          @click="sendTestEmail"
          :disabled="sending || !campaignData.subject"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {{ sending ? 'Sending...' : 'Send Test' }}
        </button>
        <button
          @click="confirmSendCampaign"
          :disabled="sending || !campaignData.subject"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Send Campaign
        </button>
      </div>
    </div>

    <!-- Drafts Panel -->
    <div v-if="showDraftsPanel" class="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-gray-900">Saved Drafts</h3>
        <button
          @click="newDraft"
          class="text-sm text-blue-600 hover:text-blue-700"
        >
          + New Draft
        </button>
      </div>

      <div v-if="loadingDrafts" class="text-center py-4 text-gray-500">
        Loading drafts...
      </div>

      <div v-else-if="drafts.length === 0" class="text-center py-4 text-gray-500">
        No saved drafts yet
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div
          v-for="draft in drafts"
          :key="draft._id"
          :class="[
            'p-3 rounded-lg border cursor-pointer transition-all',
            currentDraftId === draft._id
              ? 'border-yellow-500 bg-yellow-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          ]"
          @click="loadDraft(draft)"
        >
          <div class="flex justify-between items-start">
            <div class="flex-1 min-w-0">
              <p class="font-medium text-gray-900 truncate">{{ draft.name }}</p>
              <p class="text-xs text-gray-500 truncate">{{ draft.campaignData?.subject || 'No subject' }}</p>
              <p class="text-xs text-gray-400 mt-1">
                {{ formatDate(draft.updatedAt) }}
              </p>
            </div>
            <button
              @click.stop="deleteDraft(draft._id)"
              class="ml-2 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete draft"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Campaign Editor -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left Column: Editor -->
      <div class="space-y-6">
        <!-- Segment Selection -->
        <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <label class="block text-sm font-medium text-gray-700 mb-2">Send To (Segment)</label>
          <div v-if="loadingSegments" class="text-sm text-gray-500">Loading segments...</div>
          <div v-else-if="segments.length === 0" class="text-sm text-gray-500">
            No segments found. <a href="https://resend.com/segments" target="_blank" class="text-blue-600 underline">Create one in Resend</a>
          </div>
          <select
            v-else
            v-model="selectedSegmentId"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a segment...</option>
            <option v-for="segment in segments" :key="segment.id" :value="segment.id">
              {{ segment.name }}
            </option>
          </select>
          <p class="text-xs text-gray-500 mt-1">
            <button @click="fetchSegments" class="text-blue-600 hover:underline">Refresh list</button>
          </p>
        </div>

        <!-- Subject Line & Campaign Name -->
        <div class="bg-gray-50 rounded-lg p-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
          <input
            v-model="campaignData.subject"
            type="text"
            placeholder="e.g., New Release: The Forgotten Casks"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
          />
          <div class="mt-3">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Campaign Name (for Google Analytics)
            </label>
            <input
              v-model="campaignData.campaignName"
              type="text"
              :placeholder="generatedCampaignName"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent"
            />
            <p class="text-xs text-gray-500 mt-1">
              Auto-generated: <code class="bg-gray-200 px-1 rounded">{{ generatedCampaignName }}</code>
            </p>
          </div>
        </div>

        <!-- Design Settings -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Design Settings</h3>

          <div class="space-y-4">
            <!-- Background Color -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
              <div class="flex items-center space-x-3">
                <input
                  v-model="campaignData.backgroundColor"
                  type="color"
                  class="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  v-model="campaignData.backgroundColor"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  @click="campaignData.backgroundColor = '#1a1a1a'"
                  class="px-3 py-2 text-xs bg-gray-800 text-white rounded"
                >
                  Dark
                </button>
                <button
                  @click="campaignData.backgroundColor = '#ffffff'"
                  class="px-3 py-2 text-xs bg-white border border-gray-300 rounded"
                >
                  Light
                </button>
              </div>
            </div>

            <!-- Headline Color -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Headline Color</label>
              <div class="flex items-center space-x-3">
                <input
                  v-model="campaignData.headlineColor"
                  type="color"
                  class="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  v-model="campaignData.headlineColor"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  @click="campaignData.headlineColor = '#ffffff'"
                  class="px-3 py-2 text-xs bg-gray-800 text-white rounded"
                >
                  White
                </button>
                <button
                  @click="campaignData.headlineColor = '#1a1a1a'"
                  class="px-3 py-2 text-xs bg-white border border-gray-300 rounded"
                >
                  Black
                </button>
              </div>
            </div>

            <!-- Body Text Color -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Body Text Color</label>
              <div class="flex items-center space-x-3">
                <input
                  v-model="campaignData.bodyTextColor"
                  type="color"
                  class="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  v-model="campaignData.bodyTextColor"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  @click="campaignData.bodyTextColor = '#ffffff'"
                  class="px-3 py-2 text-xs bg-gray-800 text-white rounded"
                >
                  White
                </button>
                <button
                  @click="campaignData.bodyTextColor = '#1a1a1a'"
                  class="px-3 py-2 text-xs bg-white border border-gray-300 rounded"
                >
                  Black
                </button>
              </div>
            </div>

            <!-- Link Color -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Link Color</label>
              <div class="flex items-center space-x-3">
                <input
                  v-model="campaignData.linkColor"
                  type="color"
                  class="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  v-model="campaignData.linkColor"
                  type="text"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  @click="campaignData.linkColor = '#C89B78'"
                  class="px-3 py-2 text-xs text-white rounded" style="background-color: #C89B78;"
                >
                  Gold
                </button>
                <button
                  @click="campaignData.linkColor = '#ffffff'"
                  class="px-3 py-2 text-xs bg-gray-800 text-white rounded"
                >
                  White
                </button>
              </div>
            </div>

            <!-- Header Image -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Header Image</label>
              <select
                v-model="campaignData.headerImage"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">No header image</option>
                <option v-for="img in headerImages" :key="img.value" :value="img.value">
                  {{ img.label }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">Or enter custom URL:</p>
              <input
                v-model="campaignData.headerImageCustom"
                type="text"
                placeholder="https://83islands.com/image/..."
                class="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p class="text-xs text-gray-500 mt-2">Link image to (optional):</p>
              <input
                v-model="campaignData.headerImageLink"
                type="text"
                placeholder="https://83islands.com/..."
                class="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <!-- Show Logo -->
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700">Show Logo Below Image</label>
              <label class="flex items-center">
                <input
                  v-model="campaignData.showLogo"
                  type="checkbox"
                  class="rounded border-gray-300 text-gold focus:ring-gold"
                />
                <span class="ml-2 text-sm text-gray-600">Show</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Main Body Content -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Main Content</h3>

          <div class="space-y-4">
            <!-- Personalized Greeting -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Personalized Greeting</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showGreeting"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Include</span>
                </label>
              </div>
              <div v-if="campaignData.showGreeting" class="space-y-2">
                <div class="grid grid-cols-2 gap-3">
                  <input
                    v-model="campaignData.greetingPrefix"
                    type="text"
                    placeholder="Hi"
                    class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <input
                    v-model="campaignData.greetingDefault"
                    type="text"
                    placeholder="there"
                    class="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <select
                  v-model="campaignData.greetingStyle"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="static">Static (no personalization)</option>
                  <option value="resend_triple">Triple braces with default</option>
                  <option value="resend_double">Double braces (no default)</option>
                  <option value="liquid">Liquid style with default</option>
                </select>
                <p class="text-xs text-gray-500">
                  Preview: "{{ greetingPreview }}"
                </p>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Headline</label>
              <input
                v-model="campaignData.headline"
                type="text"
                placeholder="e.g., Introducing Our Latest Release"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Body Text</label>
              <textarea
                v-model="campaignData.bodyText"
                rows="6"
                placeholder="Write your email content here..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                Tip: Use blank lines for paragraphs. For links use: <code class="bg-gray-200 px-1 rounded">[link text](URL)</code>
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Call-to-Action Button</label>
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="campaignData.ctaText"
                  type="text"
                  placeholder="Button text"
                  class="px-3 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  v-model="campaignData.ctaUrl"
                  type="text"
                  placeholder="https://83islands.com/..."
                  class="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <!-- Icon Buttons -->
            <div>
              <div class="flex items-center justify-between mb-3">
                <label class="text-sm font-medium text-gray-700">Quick Action Buttons</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showIconButtons"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Show</span>
                </label>
              </div>
              <div v-if="campaignData.showIconButtons" class="space-y-2">
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-600 w-24">Product Info</span>
                  <input
                    v-model="campaignData.productInfoUrl"
                    type="text"
                    placeholder="https://83islands.com/collections"
                    class="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-600 w-24">Where to Buy</span>
                  <input
                    v-model="campaignData.buyUrl"
                    type="text"
                    placeholder="https://83islands.com/buy"
                    class="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-600 w-24">Experience It</span>
                  <input
                    v-model="campaignData.tourUrl"
                    type="text"
                    placeholder="https://83islands.com/tours"
                    class="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Secondary Content -->
        <div class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">Secondary Content</h3>
            <label class="flex items-center">
              <input
                v-model="campaignData.showSecondaryContent"
                type="checkbox"
                class="rounded border-gray-300 text-gold focus:ring-gold"
              />
              <span class="ml-2 text-sm text-gray-600">Show</span>
            </label>
          </div>

          <div v-if="campaignData.showSecondaryContent" class="space-y-4">
            <!-- Secondary Image -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Image</label>
              <select
                v-model="campaignData.secondaryImage"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">No image</option>
                <option v-for="img in headerImages" :key="img.value" :value="img.value">
                  {{ img.label }}
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">Or enter custom URL:</p>
              <input
                v-model="campaignData.secondaryImageCustom"
                type="text"
                placeholder="https://83islands.com/image/..."
                class="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p class="text-xs text-gray-500 mt-2">Link image to (optional):</p>
              <input
                v-model="campaignData.secondaryImageLink"
                type="text"
                placeholder="https://83islands.com/..."
                class="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Headline</label>
              <input
                v-model="campaignData.secondaryHeadline"
                type="text"
                placeholder="e.g., Also Worth Exploring"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Body Text</label>
              <textarea
                v-model="campaignData.secondaryBodyText"
                rows="6"
                placeholder="Write your secondary content here..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none"
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                Tip: Use blank lines for paragraphs. For links use: <code class="bg-gray-200 px-1 rounded">[link text](URL)</code>
              </p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Call-to-Action Button</label>
              <div class="grid grid-cols-2 gap-3">
                <input
                  v-model="campaignData.secondaryCtaText"
                  type="text"
                  placeholder="Button text"
                  class="px-3 py-2 border border-gray-300 rounded-lg"
                />
                <input
                  v-model="campaignData.secondaryCtaUrl"
                  type="text"
                  placeholder="https://83islands.com/..."
                  class="px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom Sections (Semi-permanent) -->
        <div class="bg-gray-50 rounded-lg p-4">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Bottom Sections</h3>

          <div class="space-y-4">
            <!-- Second Image (Visit Us) -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Second Image (Visit Us)</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showSecondImage"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Show</span>
                </label>
              </div>
              <div v-if="campaignData.showSecondImage" class="space-y-2">
                <select
                  v-model="campaignData.secondImage"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select an image...</option>
                  <option v-for="img in headerImages" :key="img.value" :value="img.value">
                    {{ img.label }}
                  </option>
                </select>
                <input
                  v-model="campaignData.secondImage"
                  type="text"
                  placeholder="Or enter custom image URL..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <input
                  v-model="campaignData.secondImageLink"
                  type="text"
                  placeholder="Link URL (e.g., https://83islands.com/tours)"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <!-- Merchandise Section -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Merchandise</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showMerch"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Show</span>
                </label>
              </div>
              <div v-if="campaignData.showMerch" class="space-y-2">
                <input
                  v-model="campaignData.merchImage"
                  type="text"
                  placeholder="Merch image URL"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <textarea
                  v-model="campaignData.merchText"
                  rows="2"
                  placeholder="Merchandise description..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                ></textarea>
              </div>
            </div>

            <!-- Events Section -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Upcoming Events</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showEvents"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Show</span>
                </label>
              </div>
              <div v-if="campaignData.showEvents" class="space-y-2">
                <input
                  v-model="campaignData.eventsImage"
                  type="text"
                  placeholder="Events image URL"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <textarea
                  v-model="campaignData.eventsText"
                  rows="2"
                  placeholder="Events description..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                ></textarea>
              </div>
            </div>

            <!-- Book a Tour Section -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-700">Book a Tour CTA</label>
                <label class="flex items-center">
                  <input
                    v-model="campaignData.showBooking"
                    type="checkbox"
                    class="rounded border-gray-300 text-gold focus:ring-gold"
                  />
                  <span class="ml-2 text-sm text-gray-600">Show</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Live Preview -->
      <div class="bg-gray-100 rounded-lg p-4 sticky top-4">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Live Preview</h3>
        <div class="bg-white rounded-lg shadow-lg overflow-hidden max-h-[800px] overflow-y-auto">
          <div v-html="generateEmailHTML()" class="email-preview"></div>
        </div>
      </div>
    </div>

    <!-- Preview Modal -->
    <div v-if="showPreview" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div class="flex justify-between items-center p-4 border-b">
          <h3 class="text-lg font-bold">Email Preview</h3>
          <button @click="showPreview = false" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-4 bg-gray-100 overflow-y-auto flex-1">
          <div class="bg-white mx-auto" style="max-width: 600px;">
            <div v-html="generateEmailHTML()"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmation" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4">Confirm Send Campaign</h3>
        <p class="text-gray-600 mb-4">
          You are about to send this email to all subscribers in:
          <span class="font-bold text-blue-600">{{ selectedSegmentName }}</span>
        </p>
        <p class="text-sm text-gray-500 mb-6">
          Subject: <span class="font-medium">{{ campaignData.subject }}</span>
        </p>
        <div class="flex justify-end space-x-3">
          <button
            @click="showConfirmation = false"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="sendCampaign"
            :disabled="sending"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {{ sending ? 'Sending...' : 'Send to All' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Save Draft Modal -->
    <div v-if="showSaveModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-bold text-gray-900 mb-4">Save Draft</h3>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Draft Name</label>
          <input
            v-model="draftName"
            type="text"
            placeholder="e.g., December Newsletter"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            @keyup.enter="confirmSaveDraft"
          />
        </div>
        <div class="flex justify-end space-x-3">
          <button
            @click="showSaveModal = false"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            @click="confirmSaveDraft"
            :disabled="!draftName || saving"
            class="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Status Messages -->
    <div v-if="statusMessage" :class="[
      'fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50',
      statusType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    ]">
      {{ statusMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'

const sending = ref(false)
const saving = ref(false)
const showPreview = ref(false)
const showConfirmation = ref(false)
const showSaveModal = ref(false)
const showDraftsPanel = ref(false)
const statusMessage = ref('')
const statusType = ref('success')

// Draft management
const drafts = ref([])
const loadingDrafts = ref(false)
const currentDraftId = ref(null)
const currentDraftName = ref('')
const draftName = ref('')
const lastSavedData = ref(null)

// Segment management (formerly audiences)
const segments = ref([])
const loadingSegments = ref(false)
const selectedSegmentId = ref('')

const selectedSegmentName = computed(() => {
  const segment = segments.value.find(s => s.id === selectedSegmentId.value)
  return segment?.name || 'No segment selected'
})

const headerImages = [
  { value: 'https://83islands.com/image/83islands_storian.jpg', label: 'Storian Collection' },
  { value: 'https://83islands.com/image/83islands_location.jpg', label: 'Distillery Location' },
  { value: 'https://83islands.com/image/83islands_sugarcane.jpg', label: 'Sugarcane Fields' },
  { value: 'https://83islands.com/image/productionfloor.jpg', label: 'Production Floor' },
  { value: 'https://83islands.com/image/83islands-jungle.jpg', label: 'Vanuatu Jungle' },
  { value: 'https://83islands.com/image/83islands_refracto.jpg', label: 'Refractometer' },
]

const campaignData = reactive({
  subject: '',
  campaignName: '', // For Google Analytics UTM tracking
  backgroundColor: '#1a1a1a',
  headlineColor: '#ffffff',
  bodyTextColor: '#ffffff',
  linkColor: '#C89B78',
  headerImage: 'https://83islands.com/image/83islands_storian.jpg',
  headerImageCustom: '',
  headerImageLink: '',
  showLogo: true,
  // Personalized greeting
  showGreeting: true,
  greetingPrefix: 'Hi',
  greetingDefault: 'there',
  greetingStyle: 'resend_triple', // static, resend_triple, resend_double, liquid
  headline: '',
  bodyText: '',
  ctaText: 'Learn More',
  ctaUrl: 'https://83islands.com',
  // Three icon buttons
  showIconButtons: true,
  productInfoUrl: 'https://83islands.com/collections',
  buyUrl: 'https://83islands.com/buy',
  tourUrl: 'https://83islands.com/tours',
  showMerch: true,
  merchText: `T-shirts, glasses, barware and more available at our distillery shop.`,
  merchImage: 'https://83islands.com/image/83islands-Laentshirt.png',
  showEvents: true,
  eventsText: `83 Islands Night - Every Friday\nJoin us for live music, cocktails, and good vibes at the distillery.`,
  eventsImage: '',
  // Secondary content section
  showSecondaryContent: false,
  secondaryImage: '',
  secondaryImageCustom: '',
  secondaryImageLink: '',
  secondaryHeadline: '',
  secondaryBodyText: '',
  secondaryCtaText: '',
  secondaryCtaUrl: '',
  // Second image section (before merch/events)
  showSecondImage: true,
  secondImage: '',
  secondImageLink: 'https://83islands.com/tours',
  showBooking: true,
})

// Generate campaign name from subject for UTM tracking
const generatedCampaignName = computed(() => {
  if (!campaignData.subject) return 'email_campaign'
  const date = new Date()
  const monthYear = `${date.toLocaleString('en', { month: 'short' }).toLowerCase()}${date.getFullYear()}`
  // Convert subject to URL-friendly format: lowercase, replace spaces with underscores, remove special chars
  const slugified = campaignData.subject
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 40)
  return `${slugified}_${monthYear}`
})

// Get the campaign name (custom or auto-generated)
const getCampaignName = computed(() => {
  return campaignData.campaignName || generatedCampaignName.value
})

// Add UTM parameters to a URL
const addUtmParams = (url, content) => {
  if (!url) return url
  // Don't add UTM to unsubscribe or mailto links
  if (url.includes('RESEND_UNSUBSCRIBE') || url.startsWith('mailto:')) return url

  try {
    const urlObj = new URL(url)
    urlObj.searchParams.set('utm_source', 'newsletter')
    urlObj.searchParams.set('utm_medium', 'email')
    urlObj.searchParams.set('utm_campaign', getCampaignName.value)
    if (content) {
      urlObj.searchParams.set('utm_content', content)
    }
    return urlObj.toString()
  } catch (e) {
    // If URL parsing fails, append manually
    const separator = url.includes('?') ? '&' : '?'
    let params = `utm_source=newsletter&utm_medium=email&utm_campaign=${encodeURIComponent(getCampaignName.value)}`
    if (content) {
      params += `&utm_content=${encodeURIComponent(content)}`
    }
    return `${url}${separator}${params}`
  }
}

const getHeaderImageUrl = computed(() => {
  return campaignData.headerImageCustom || campaignData.headerImage
})

// Generate greeting based on selected style
const getGreetingVariable = computed(() => {
  const prefix = campaignData.greetingPrefix || 'Hi'
  const defaultName = campaignData.greetingDefault || 'there'

  switch (campaignData.greetingStyle) {
    case 'static':
      return `${prefix} ${defaultName},`
    case 'resend_triple':
      return `${prefix} {{{first_name|${defaultName}}}},`
    case 'resend_double':
      return `${prefix} {{first_name}},`
    case 'liquid':
      return `${prefix} {{ first_name | default: '${defaultName}' }},`
    default:
      return `${prefix} ${defaultName},`
  }
})

// Preview for UI
const greetingPreview = computed(() => {
  const prefix = campaignData.greetingPrefix || 'Hi'
  const defaultName = campaignData.greetingDefault || 'there'

  switch (campaignData.greetingStyle) {
    case 'static':
      return `${prefix} ${defaultName},`
    case 'resend_triple':
      return `${prefix} Pierre-Luc, (or "${prefix} ${defaultName}," if no name)`
    case 'resend_double':
      return `${prefix} Pierre-Luc, (blank if no name)`
    case 'liquid':
      return `${prefix} Pierre-Luc, (or "${prefix} ${defaultName}," if no name)`
    default:
      return `${prefix} ${defaultName},`
  }
})

// Convert markdown-style links [text](url) to HTML anchor tags with UTM tracking
const parseLinks = (text, linkColor) => {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    const trackedUrl = addUtmParams(url, 'body_link')
    return `<a href="${trackedUrl}" style="color: ${linkColor}; text-decoration: underline;">${linkText}</a>`
  })
}

const generateEmailHTML = () => {
  const bgColor = campaignData.backgroundColor
  const headlineColor = campaignData.headlineColor || '#ffffff'
  const bodyTextColor = campaignData.bodyTextColor || '#ffffff'
  const linkColor = campaignData.linkColor || '#C89B78'
  const mutedColor = bgColor === '#ffffff' || bgColor === '#fff' ? '#6b7280' : '#9ca3af'

  const bodyParagraphs = campaignData.bodyText
    .split('\n\n')
    .filter(p => p.trim())
    .map(p => {
      const withLinks = parseLinks(p.replace(/\n/g, '<br>'), linkColor)
      return `<p style="margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: ${bodyTextColor};">${withLinks}</p>`
    })
    .join('')

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; background-color: ${bgColor}; font-family: Arial, sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: ${bgColor};">
    <tr>
      <td align="center">
        <!-- Header Image - Max 600px Width -->
        ${getHeaderImageUrl.value ? `
        <table width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; width: 100%;">
          <tr>
            <td>
              ${campaignData.headerImageLink ? `<a href="${addUtmParams(campaignData.headerImageLink, 'header_image')}" target="_blank">` : ''}
              <img src="${getHeaderImageUrl.value}" alt="83 Islands" style="width: 100%; max-width: 600px; height: auto; display: block;">
              ${campaignData.headerImageLink ? `</a>` : ''}
            </td>
          </tr>
        </table>
        ` : ''}

        <!-- Content Container -->
        <table width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; width: 100%;">

          <!-- Logo -->
          ${campaignData.showLogo ? `
          <tr>
            <td align="center" style="padding: 30px 20px 20px 20px;">
              <img src="https://83islands.com/image/83IslandsDistillery.svg" alt="83 Islands Distillery" style="width: 150px; height: auto;">
            </td>
          </tr>
          ` : ''}

          <!-- Headline -->
          ${campaignData.headline ? `
          <tr>
            <td align="center" style="padding: 0 20px 20px 20px;">
              <h1 style="margin: 0; font-size: 28px; font-weight: bold; color: ${headlineColor}; text-transform: uppercase; letter-spacing: 2px;">
                ${campaignData.headline}
              </h1>
            </td>
          </tr>
          ` : ''}

          <!-- Body Content -->
          <tr>
            <td style="padding: 20px 30px; color: ${bodyTextColor};">
              ${campaignData.showGreeting ? `<p style="margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: ${bodyTextColor};">${getGreetingVariable.value}</p>` : ''}
              ${bodyParagraphs || '<p style="margin: 0; font-size: 16px; color: ' + mutedColor + ';">Your email content will appear here...</p>'}
            </td>
          </tr>

          <!-- CTA Button -->
          ${campaignData.ctaText && campaignData.ctaUrl ? `
          <tr>
            <td align="center" style="padding: 20px;">
              <a href="${addUtmParams(campaignData.ctaUrl, 'cta_button')}" style="display: inline-block; padding: 14px 32px; background-color: #C89B78; color: #ffffff; text-decoration: none; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px;">
                ${campaignData.ctaText}
              </a>
            </td>
          </tr>
          ` : ''}

          
          <!-- Secondary Content Section -->
          ${campaignData.showSecondaryContent ? (() => {
            const secImageUrl = campaignData.secondaryImageCustom || campaignData.secondaryImage
            const secBodyParagraphs = campaignData.secondaryBodyText
              ? campaignData.secondaryBodyText
                  .split('\n\n')
                  .filter(p => p.trim())
                  .map(p => {
                    const withLinks = parseLinks(p.replace(/\n/g, '<br>'), linkColor)
                    return `<p style="margin: 0 0 16px 0; font-size: 16px; line-height: 1.6; color: ${bodyTextColor};">${withLinks}</p>`
                  })
                  .join('')
              : ''
            return `
          <!-- Secondary Image -->
          ${secImageUrl ? `
          <tr>
            <td align="center" style="padding: 20px 0 0 0;">
              ${campaignData.secondaryImageLink ? `<a href="${addUtmParams(campaignData.secondaryImageLink, 'secondary_image')}" target="_blank">` : ''}
              <img src="${secImageUrl}" alt="" style="width: 100%; max-width: 600px; height: auto; display: block;">
              ${campaignData.secondaryImageLink ? `</a>` : ''}
            </td>
          </tr>
          ` : ''}

          <!-- Secondary Headline -->
          ${campaignData.secondaryHeadline ? `
          <tr>
            <td align="center" style="padding: 20px 20px 10px 20px;">
              <h2 style="margin: 0; font-size: 24px; font-weight: bold; color: ${headlineColor}; text-transform: uppercase; letter-spacing: 2px;">
                ${campaignData.secondaryHeadline}
              </h2>
            </td>
          </tr>
          ` : ''}

          <!-- Secondary Body Content -->
          ${secBodyParagraphs ? `
          <tr>
            <td style="padding: 10px 30px 20px 30px; color: ${bodyTextColor};">
              ${secBodyParagraphs}
            </td>
          </tr>
          ` : ''}

          <!-- Secondary CTA Button -->
          ${campaignData.secondaryCtaText && campaignData.secondaryCtaUrl ? `
          <tr>
            <td align="center" style="padding: 10px 20px 20px 20px;">
              <a href="${addUtmParams(campaignData.secondaryCtaUrl, 'secondary_cta_button')}" style="display: inline-block; padding: 14px 32px; background-color: #C89B78; color: #ffffff; text-decoration: none; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px;">
                ${campaignData.secondaryCtaText}
              </a>
            </td>
          </tr>
          ` : ''}
          `
          })() : ''}

          <!-- Second Image Section (Visit Us) -->
          ${campaignData.showSecondImage && campaignData.secondImage ? `
          <tr>
            <td align="center" style="padding: 0;">
              ${campaignData.secondImageLink ? `<a href="${addUtmParams(campaignData.secondImageLink, 'second_image')}" target="_blank">` : ''}
              <img src="${campaignData.secondImage}" alt="Visit Us" style="width: 100%; max-width: 600px; height: auto; display: block;">
              ${campaignData.secondImageLink ? `</a>` : ''}
            </td>
          </tr>
          ` : ''}

          <!-- Two Column Section: Merch & Events -->
          ${campaignData.showMerch || campaignData.showEvents ? `
          <tr>
            <td style="padding: 20px;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  ${campaignData.showMerch ? `
                  <td width="50%" valign="top" style="padding: 10px;">
                    ${campaignData.merchImage ? `
                    <img src="${campaignData.merchImage}" alt="Merchandise" style="width: 100%; max-width: 200px; height: auto; display: block; margin: 0 auto 15px auto;">
                    ` : ''}
                    <h3 style="margin: 0 0 10px 0; font-size: 14px; font-weight: bold; color: #ffffff; text-transform: uppercase; letter-spacing: 1px; text-align: center;">
                      Merchandise
                    </h3>
                    <p style="margin: 0; font-size: 13px; line-height: 1.5; color: #ffffff; text-align: center;">
                      ${campaignData.merchText.replace(/\n/g, '<br>')}
                    </p>
                  </td>
                  ` : ''}
                  ${campaignData.showEvents ? `
                  <td width="50%" valign="top" style="padding: 10px;">
                    ${campaignData.eventsImage ? `
                    <img src="${campaignData.eventsImage}" alt="Events" style="width: 100%; max-width: 200px; height: auto; display: block; margin: 0 auto 15px auto;">
                    ` : ''}
                    <h3 style="margin: 0 0 10px 0; font-size: 14px; font-weight: bold; color: #ffffff; text-transform: uppercase; letter-spacing: 1px; text-align: center;">
                      Events
                    </h3>
                    <p style="margin: 0; font-size: 13px; line-height: 1.5; color: #ffffff; text-align: center;">
                      ${campaignData.eventsText.replace(/\n/g, '<br>')}
                    </p>
                  </td>
                  ` : ''}
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}

          <!-- Book a Tour CTA -->
          ${campaignData.showBooking ? `
          <tr>
            <td align="center" style="padding: 30px 20px;">
              <table cellpadding="0" cellspacing="0" style="background-color: #C89B78; border-radius: 4px;">
                <tr>
                  <td style="padding: 20px 40px;">
                    <p style="margin: 0 0 10px 0; font-size: 14px; color: #ffffff; text-transform: uppercase; letter-spacing: 1px;">
                      Visit Us in Vanuatu
                    </p>
                    <a href="${addUtmParams('https://83islands.com/tours', 'book_tour_button')}" style="display: inline-block; padding: 12px 24px; background-color: #1a1a1a; color: #ffffff; text-decoration: none; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; border-radius: 4px;">
                      Book a Tour
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}

          <!-- Icon Buttons -->
          ${campaignData.showIconButtons ? `
          <tr>
            <td align="center" style="padding: 30px 10px;">
              <table cellpadding="0" cellspacing="0">
                <tr>
                  <td align="center" style="padding: 0 20px;">
                    <a href="${addUtmParams(campaignData.productInfoUrl, 'icon_product')}" style="text-decoration: none; display: inline-block;">
                      <img src="https://83islands.com/image/bottle-white.png" alt="Product" style="width: 32px; height: 32px; display: block; margin: 0 auto 8px auto;" />
                      <span style="font-size: 11px; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px; display: block;">Product Info</span>
                    </a>
                  </td>
                  <td align="center" style="padding: 0 20px;">
                    <a href="${addUtmParams(campaignData.buyUrl, 'icon_buy')}" style="text-decoration: none; display: inline-block;">
                      <img src="https://83islands.com/image/shop-white.png" alt="Buy" style="width: 32px; height: 32px; display: block; margin: 0 auto 8px auto;" />
                      <span style="font-size: 11px; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px; display: block;">Where to Buy</span>
                    </a>
                  </td>
                  <td align="center" style="padding: 0 20px;">
                    <a href="${addUtmParams(campaignData.tourUrl, 'icon_experience')}" style="text-decoration: none; display: inline-block;">
                      <img src="https://83islands.com/image/cocktail-white.png" alt="Experience" style="width: 32px; height: 32px; display: block; margin: 0 auto 8px auto;" />
                      <span style="font-size: 11px; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px; display: block;">Experience It</span>
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          ` : ''}

          <!-- Footer -->
          <tr>
            <td style="padding: 30px 20px; border-top: 1px solid ${mutedColor};">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td align="center">
                    <p style="margin: 0 0 10px 0; font-size: 12px; color: #ffffff;">
                      83 Islands Distillery, Stella Mare, Port Vila, Vanuatu, PO1276
                    </p>
                    <p style="margin: 0 0 10px 0; font-size: 12px; color: #ffffff;">
                      <a href="${addUtmParams('https://83islands.com', 'footer_website')}" style="color: ${linkColor}; text-decoration: none;">Website</a> |
                      <a href="${addUtmParams('https://instagram.com/83islands', 'footer_instagram')}" style="color: ${linkColor}; text-decoration: none;">Instagram</a> |
                      <a href="${addUtmParams('https://www.facebook.com/83IslandsDistillery/', 'footer_facebook')}" style="color: ${linkColor}; text-decoration: none;">Facebook</a>
                    </p>
                    <p style="margin: 0; font-size: 11px; color: #ffffff;">
                      <a href="{{{RESEND_UNSUBSCRIBE_URL}}}" style="color: #ffffff; text-decoration: underline;">Unsubscribe</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}

const showStatus = (message, type = 'success') => {
  statusMessage.value = message
  statusType.value = type
  setTimeout(() => {
    statusMessage.value = ''
  }, 5000)
}

const sendTestEmail = async () => {
  sending.value = true
  try {
    const response = await fetch('/api/email-campaign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({
        type: 'test',
        subject: `[TEST] ${campaignData.subject}`,
        html: generateEmailHTML()
      })
    })

    const data = await response.json()
    if (data.success) {
      showStatus('Test email sent successfully!')
    } else {
      showStatus(data.message || 'Failed to send test email', 'error')
    }
  } catch (error) {
    console.error('Error sending test email:', error)
    showStatus('Failed to send test email', 'error')
  } finally {
    sending.value = false
  }
}

const confirmSendCampaign = () => {
  showConfirmation.value = true
}

const sendCampaign = async () => {
  if (!selectedSegmentId.value) {
    showStatus('Please select a segment to send to', 'error')
    return
  }

  sending.value = true
  try {
    const response = await fetch('/api/email-campaign', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({
        type: 'broadcast',
        subject: campaignData.subject,
        html: generateEmailHTML(),
        segmentId: selectedSegmentId.value
      })
    })

    const data = await response.json()
    if (data.success) {
      showStatus(`Campaign sent to ${data.recipientCount || 'all'} subscribers!`)
      showConfirmation.value = false
    } else {
      showStatus(data.message || 'Failed to send campaign', 'error')
    }
  } catch (error) {
    console.error('Error sending campaign:', error)
    showStatus('Failed to send campaign', 'error')
  } finally {
    sending.value = false
  }
}

// Draft Management Functions
const hasUnsavedChanges = computed(() => {
  if (!lastSavedData.value) return campaignData.subject !== ''
  return JSON.stringify(campaignData) !== JSON.stringify(lastSavedData.value)
})

const fetchDrafts = async () => {
  loadingDrafts.value = true
  try {
    const response = await fetch('/api/email-drafts', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })
    const data = await response.json()
    if (data.success) {
      drafts.value = data.drafts
    }
  } catch (error) {
    console.error('Error fetching drafts:', error)
  } finally {
    loadingDrafts.value = false
  }
}

const saveDraft = () => {
  if (currentDraftId.value) {
    // Update existing draft
    draftName.value = currentDraftName.value
  } else {
    // New draft - suggest name from subject
    draftName.value = campaignData.subject || ''
  }
  showSaveModal.value = true
}

const confirmSaveDraft = async () => {
  if (!draftName.value) return

  saving.value = true
  try {
    const url = currentDraftId.value
      ? `/api/email-drafts?id=${currentDraftId.value}`
      : '/api/email-drafts'

    const method = currentDraftId.value ? 'PUT' : 'POST'

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({
        name: draftName.value,
        campaignData: { ...campaignData }
      })
    })

    const data = await response.json()
    if (data.success) {
      if (!currentDraftId.value && data.draftId) {
        currentDraftId.value = data.draftId
      }
      currentDraftName.value = draftName.value
      lastSavedData.value = { ...campaignData }
      showStatus('Draft saved!')
      showSaveModal.value = false
      fetchDrafts()
    } else {
      showStatus(data.message || 'Failed to save draft', 'error')
    }
  } catch (error) {
    console.error('Error saving draft:', error)
    showStatus('Failed to save draft', 'error')
  } finally {
    saving.value = false
  }
}

const loadDraft = (draft) => {
  if (hasUnsavedChanges.value) {
    if (!confirm('You have unsaved changes. Load this draft anyway?')) {
      return
    }
  }

  // Load draft data into campaignData
  Object.keys(draft.campaignData).forEach(key => {
    if (key in campaignData) {
      campaignData[key] = draft.campaignData[key]
    }
  })

  currentDraftId.value = draft._id
  currentDraftName.value = draft.name
  lastSavedData.value = { ...draft.campaignData }

  showStatus(`Loaded: ${draft.name}`)
}

const deleteDraft = async (draftId) => {
  if (!confirm('Are you sure you want to delete this draft?')) {
    return
  }

  try {
    const response = await fetch(`/api/email-drafts?id=${draftId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })

    const data = await response.json()
    if (data.success) {
      if (currentDraftId.value === draftId) {
        currentDraftId.value = null
        currentDraftName.value = ''
      }
      showStatus('Draft deleted')
      fetchDrafts()
    } else {
      showStatus(data.message || 'Failed to delete draft', 'error')
    }
  } catch (error) {
    console.error('Error deleting draft:', error)
    showStatus('Failed to delete draft', 'error')
  }
}

const newDraft = () => {
  if (hasUnsavedChanges.value) {
    if (!confirm('You have unsaved changes. Start a new draft anyway?')) {
      return
    }
  }

  // Reset to defaults
  campaignData.subject = ''
  campaignData.campaignName = ''
  campaignData.backgroundColor = '#1a1a1a'
  campaignData.headlineColor = '#ffffff'
  campaignData.bodyTextColor = '#ffffff'
  campaignData.linkColor = '#C89B78'
  campaignData.headerImage = 'https://83islands.com/image/83islands_storian.jpg'
  campaignData.headerImageCustom = ''
  campaignData.headerImageLink = ''
  campaignData.showLogo = true
  campaignData.showGreeting = true
  campaignData.greetingPrefix = 'Hi'
  campaignData.greetingDefault = 'there'
  campaignData.greetingStyle = 'resend_triple'
  campaignData.headline = ''
  campaignData.bodyText = ''
  campaignData.ctaText = 'Learn More'
  campaignData.ctaUrl = 'https://83islands.com'
  campaignData.showIconButtons = true
  campaignData.productInfoUrl = 'https://83islands.com/collections'
  campaignData.buyUrl = 'https://83islands.com/buy'
  campaignData.tourUrl = 'https://83islands.com/tours'
  campaignData.showMerch = true
  campaignData.merchText = `T-shirts, glasses, barware and more available at our distillery shop.`
  campaignData.merchImage = 'https://83islands.com/image/83islands-Laentshirt.png'
  campaignData.showEvents = true
  campaignData.eventsText = `83 Islands Night - Every Friday\nJoin us for live music, cocktails, and good vibes at the distillery.`
  campaignData.eventsImage = ''
  campaignData.showSecondaryContent = false
  campaignData.secondaryImage = ''
  campaignData.secondaryImageCustom = ''
  campaignData.secondaryImageLink = ''
  campaignData.secondaryHeadline = ''
  campaignData.secondaryBodyText = ''
  campaignData.secondaryCtaText = ''
  campaignData.secondaryCtaUrl = ''
  campaignData.showSecondImage = true
  campaignData.secondImage = ''
  campaignData.secondImageLink = 'https://83islands.com/tours'
  campaignData.showBooking = true

  currentDraftId.value = null
  currentDraftName.value = ''
  lastSavedData.value = null
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Fetch segments from Resend
const fetchSegments = async () => {
  loadingSegments.value = true
  try {
    const response = await fetch('/api/email-campaign', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
      }
    })
    const data = await response.json()
    if (data.success) {
      segments.value = data.segments
      // Auto-select first segment if available
      if (data.segments.length > 0 && !selectedSegmentId.value) {
        selectedSegmentId.value = data.segments[0].id
      }
    }
  } catch (error) {
    console.error('Error fetching segments:', error)
  } finally {
    loadingSegments.value = false
  }
}

// Load drafts and segments on mount
onMounted(() => {
  fetchDrafts()
  fetchSegments()
})
</script>

<style scoped>
.text-gold {
  color: #C89B78;
}

.focus\:ring-gold:focus {
  --tw-ring-color: #C89B78;
}

.email-preview {
  font-size: 14px;
}

.email-preview img {
  max-width: 100%;
  height: auto;
}
</style>
